<template>
    <div class="row m-0">
        <div class="col-12">
            <div class="row headers text-dark text-center py-3">
                <h2 class="h4 m-0 mx-auto"> {{ $t("skill") }} </h2>
            </div>
            <div class="row">
                <div class="sections pb-3 px-3 w-100 text-left">
                    <div id="chartdiv" class="en-inputs w-100 ltr"></div>
                    <div class="text-center">
                        <vs-button class="d-inline mt-3" transparent active color="#22333B" data-toggle="collapse"
                                   data-target="#collapseSkills" aria-expanded="false" aria-controls="collapseSkills">
                            {{ $t("more") }}
                        </vs-button>
                    </div>
                    <div class="collapse" id="collapseSkills">
                        <span class="pt-2 d-table"> {{ $t("try") }}: </span>
                        <div id="tech-chart" class="en-inputs w-100 ltr"></div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";
import * as am4plugins_forceDirected from "@amcharts/amcharts4/plugins/forceDirected";
import am4themes_animated from "@amcharts/amcharts4/themes/animated";
import { themeState } from "../theme";

const CHART_COLORS = {
    light: { text: "#000000", barMin: "#9e9e9e", barMax: "#22333B" },
    dark: { text: "#d6dee2", barMin: "#55646b", barMax: "#4f8aa3" }
};

export default {
        name: "Skill",
        computed: {
            theme() {
                return themeState.theme;
            }
        },
        watch: {
            theme() {
                this.disposeCharts();
                this.buildCharts();
            }
        },
        created() {
            // Kept off `data` on purpose: amCharts objects must not be made reactive.
            this.amCharts = [];
        },
        mounted() {
            am4core.useTheme(am4themes_animated);
            this.buildCharts();
        },
        beforeDestroy() {
            this.disposeCharts();
        },
        methods: {
        disposeCharts() {
            this.amCharts.forEach(chart => chart.dispose());
            this.amCharts = [];
        },
        buildCharts() {
            const colors = CHART_COLORS[this.theme] || CHART_COLORS.light;

            let chart = am4core.create("chartdiv", am4charts.XYChart);
            this.amCharts.push(chart);
            chart.logo.disabled = true;
            chart.hiddenState.properties.opacity = 0;
            chart.logo.height = -300;
            chart.paddingRight = 40;

            chart.data = [
                { abbr: "F", name: "Frontend", steps: 40 },
                { abbr: "P", name: "Python", steps: 60 },
                { abbr: "P", name: "PHP", steps: 65 },
                { abbr: "G", name: "Go", steps: 50 },
                { abbr: "J", name: "JAVA", steps: 75 }
            ];

            let categoryAxis = chart.yAxes.push(new am4charts.CategoryAxis());
            categoryAxis.dataFields.category = "name";
            categoryAxis.renderer.grid.template.strokeOpacity = 0;
            categoryAxis.renderer.minGridDistance = 10;
            categoryAxis.renderer.labels.template.dx = -40;
            categoryAxis.renderer.labels.template.fill = am4core.color(colors.text);
            categoryAxis.renderer.minWidth = 120;
            categoryAxis.tooltip.disabled = true;

            let valueAxis = chart.xAxes.push(new am4charts.ValueAxis());
            valueAxis.renderer.inside = false;
            valueAxis.renderer.grid.template.strokeOpacity = 0;
            valueAxis.min = 0;
            valueAxis.max = 100;
            valueAxis.cursorTooltipEnabled = false;
            valueAxis.renderer.baseGrid.strokeOpacity = 0;
            valueAxis.renderer.labels.template.dy = 0;
            valueAxis.renderer.labels.template.fill = am4core.color(colors.text);

            let series = chart.series.push(new am4charts.ColumnSeries());
            series.dataFields.valueX = "steps";
            series.dataFields.categoryY = "name";
            series.tooltipText = "{valueX.value}";
            series.tooltip.pointerOrientation = "vertical";
            series.tooltip.dy = -10;
            series.columnsContainer.zIndex = 100;

            let columnTemplate = series.columns.template;
            columnTemplate.height = am4core.percent(20);
            columnTemplate.maxHeight = 20;
            columnTemplate.column.cornerRadius(60, 10, 60, 10);
            columnTemplate.strokeOpacity = 0;

            series.heatRules.push({
                target: columnTemplate,
                property: "fill",
                dataField: "valueX",
                min: am4core.color(colors.barMin),
                max: am4core.color(colors.barMax)
            });

            series.mainContainer.mask = undefined;

            let cursor = new am4charts.XYCursor();
            chart.cursor = cursor;
            cursor.lineX.disabled = true;
            cursor.lineY.disabled = true;
            cursor.behavior = "none";

            let bullet = columnTemplate.createChild(am4charts.CircleBullet);
            bullet.circle.radius = 15;
            bullet.valign = "middle";
            bullet.align = "left";
            bullet.isMeasured = true;
            bullet.interactionsEnabled = false;
            bullet.horizontalCenter = "right";

            let label = bullet.createChild(am4core.Label);
            label.text = "{abbr}";
            label.fontSize = 14;
            label.fill = am4core.color("#fff");
            label.horizontalCenter = "middle";
            label.verticalCenter = "middle";
            label.interactionsEnabled = false;

            chart = am4core.create("tech-chart", am4plugins_forceDirected.ForceDirectedTree);
            this.amCharts.push(chart);
            chart.logo.disabled = true;

            let networkSeries = chart.series.push(new am4plugins_forceDirected.ForceDirectedSeries());
            networkSeries.dataFields.linkWith = "linkWith";
            networkSeries.dataFields.name = "name";
            networkSeries.dataFields.id = "name";
            networkSeries.dataFields.value = "value";
            networkSeries.dataFields.children = "children";

            networkSeries.nodes.template.label.text = "{name}";
            networkSeries.fontSize = 12;
            networkSeries.linkWithStrength = 0;

            let nodeTemplate = networkSeries.nodes.template;
            nodeTemplate.tooltipText = "{name}";
            nodeTemplate.fillOpacity = 1;
            nodeTemplate.label.hideOversized = true;
            nodeTemplate.label.truncate = true;

            let linkTemplate = networkSeries.links.template;
            linkTemplate.strokeWidth = 2;
            let linkHoverState = linkTemplate.states.create("hover");
            linkHoverState.properties.strokeOpacity = 1;
            linkHoverState.properties.strokeWidth = 2;

            nodeTemplate.events.on("over", function (event) {
                let dataItem = event.target.dataItem;
                dataItem.childLinks.each(function (link) {
                    link.isHover = true;
                })
            });

            nodeTemplate.events.on("out", function (event) {
                let dataItem = event.target.dataItem;
                dataItem.childLinks.each(function (link) {
                    link.isHover = false;
                })
            });

            networkSeries.data = [
                {
                    "name": "Technologies",
                    "value": 300,
                    "children": [
                        {
                            "name": "Backend",
                            "value": 200,
                            "linkWith": ["Technologies"],
                            "children": [
                                { "name": "Spring Boot", "value": 100 },
                                { "name": "Gin", "value": 100 },
                                { "name": "Laravel", "value": 100 }
                            ]
                        }, {
                            "name": "Frontend",
                            "value": 200,
                            "linkWith": ["Technologies"],
                            "children": [
                                { "name": "Vue", "value": 100 },
                                { "name": "React", "value": 100 },
                                { "name": "Tailwind", "value": 100 },
                                { "name": "Bootstrap", "value": 100 }
                            ]
                        }, {
                            "name": "Database",
                            "value": 200,
                            "linkWith": ["Technologies"],
                            "children": [
                                { "name": "PostgreSQL", "value": 100 },
                                { "name": "MySQL", "value": 100 },
                                { "name": "Redis", "value": 100 },
                                { "name": "Elasticsearch", "value": 100 },
                                { "name": "Lucene", "value": 100 }
                            ]
                        }, {
                            "name": "AI",
                            "value": 200,
                            "linkWith": ["Technologies"],
                            "children": [
                                { "name": "GPT", "value": 100 },
                                { "name": "Claude", "value": 100 },
                                { "name": "Cursor", "value": 100 },
                                { "name": "N8N", "value": 100 }
                            ]
                        }, {
                            "name": "DS/ML",
                            "value": 200,
                            "linkWith": ["Technologies"],
                            "children": [
                                { "name": "NumPy", "value": 100 },
                                { "name": "Pandas", "value": 100 },
                                { "name": "Matplotlib", "value": 100 },
                                { "name": "SciPy", "value": 100 },
                                { "name": "PyTorch", "value": 100 }
                            ]
                        }, {
                            "name": "DevOps",
                            "value": 200,
                            "linkWith": ["Technologies"],
                            "children": [
                                { "name": "Linux", "value": 100 },
                                { "name": "Docker", "value": 100 },
                                { "name": "Kubernetes", "value": 100 },
                                { "name": "Nginx", "value": 100 }
                            ]
                        }, {
                            "name": "Tools",
                            "value": 200,
                            "linkWith": ["Technologies"],
                            "children": [
                                { "name": "Git", "value": 100 },
                                { "name": "Cloudflare", "value": 100 },
                                { "name": "WordPress", "value": 100 }
                            ]
                        }
                    ]
                }
            ];
        }
        }
    }
</script>

<style>

</style>

<style scoped>
    #chartdiv {
        height: 370px;
        background: linear-gradient(180deg, var(--card-grad-start) 0%, var(--card-grad-end) 100%);
        border-radius: 12px;
        padding: 5px 0 0 0;
        border: 1px solid var(--border);
    }

    #tech-chart {
        height: 450px;
        background: linear-gradient(180deg, var(--card-grad-start) 0%, var(--card-grad-end) 100%);
        border-radius: 12px;
        padding: 6px;
        margin-top: 10px;
        border: 1px solid var(--border);
    }

    .en-inputs span small b {
        color: var(--accent);
    }
</style>
